import type { RouteLocationRaw } from 'vue-router'

import type { AvailableLocale } from '@/i18n/types'
import type { HbBidderConfig } from '@/modules/adv/header-bidding/prebid/types'
import type { AdUnitConfig, AdvAction, OutOfPageAdType } from '@/modules/adv/types'
import type { VUE_APP_CONFIG } from '@/modules/config/createConfig'
import type { MessageAction } from '@/modules/main-frame/message-types'
import { AppNativeSDK } from '@/modules/native-app'

export type { RenderCacheContext } from './render-cache-context'

export enum AdProviderTypes {
    GAM_H5 = 'googleAd',
    GAM_INSTREAM = 'googleInstream',
    GAM_BANNER_INTERSTITIAL = 'googleBanner',
    PREBID_DIRECT = 'headerBiddingAd',

    GAME_DISTRIBUTION = 'gameDistribution',
    EMPTY = 'emptyProvider',
    APP = 'appProvider',
}

export enum AdProviderTypesNew {
    GAM_H5 = 'gam-h5',
    GAM_INSTREAM = 'gam-instream',
    GAM_BANNER_INTERSTITIAL = 'gam-banner-interstitial',
    PREBID_DIRECT = 'prebid-direct',
}

export type GameMessageAction = MessageAction | AdvAction

export type GameEvent = MessageEvent<{
    id: string
    action: GameMessageAction
    payload?: Record<string, unknown>
    responseToId?: string
    type: 'adv' | 'liveness' | 'error'
}>

export type PaginationType = {
    page: number
    pageSize: number
}

export const PLATFORM_TYPES = ['ios', 'android', 'desktop'] as const
export type PlatformType = (typeof PLATFORM_TYPES)[number]
export type ClientType = 'mobile-native' | 'mobile-web' | 'desktop-web'

export type APIGameInfoDTO = APIGameInfoType | APIRedirect

export type LeaderboardEntry = {
    rank: number
    display_name: string
    score: number
}

export type GameLeaderboard = {
    entries: LeaderboardEntry[]
    totalPlayers: number
}

export type APILeaderboardUser = {
    leaderboard: { viewer_position?: LeaderboardEntry | null } | null
}

export type APIGameInfoType = {
    game: APIGameType
    categoriesInfo: APICategoryShortInfo[]
    pathCategories: APICategoryShortInfo[]
    collectionsInfo?: APICategoryShortInfo[]
    leaderboard?: { entries?: LeaderboardEntry[]; total_players?: number }
}

type GameRedirect = {
    type: 'game'
    game_hru: string
    mechanism: string
    reason: string
}

export type APIRedirect = {
    redirectTarget:
        | {
              type: 'main-page'
              mechanism: string
              reason: string
          }
        | {
              type: 'category'
              cat_type: CategoryType
              cat_hru: string
              mechanism: string
              reason: string
          }
        | GameRedirect
    status: 301 | 302
}

export type BadgeType = 'hot' | 'new' | 'top'

type UsefulLinkType = {
    type:
        | 'WIKI'
        | 'APPLE_STORE'
        | 'GOOGLE_STORE'
        | 'FANDOM'
        | 'DISCORD'
        | 'HUAWEI_STORE'
        | 'XIAOMI_STORE'
        | 'SAMSUNG_STORE'
        | 'AMAZON_STORE'
        | 'UPTODOWN_STORE'
        | 'TAPTAP_STORE'
        | 'OTHER'
    title: string
    url: string
}

export type AgeRating = '0+' | '6+' | '12+' | '16+' | '18+'

export type SmartAdsConfig = {
    version: number
    ads?: {
        rules?: SmartAdsRule[]
        disableFullscreenBanners?: boolean
    }
}

export type SmartAdsRule = {
    type: 'interval'
    data?: {
        intervalScheduleSec?: number[]
        scheduleMode?: string
        skipIfAdShownWithinSec?: number
        skipIfAdShownWithinSecAfter?: number
        request?: string[]
    }
}

type APIGameMostRatedType = Pick<APIGameType, 'title' | 'rating' | 'preview'> & {
    game_hru: string
}

export type Rating = {
    value: number
    count: number
}

export type APIGameType = {
    id: string
    hru: string
    title: string
    icon: string
    preview: string
    big_preview: string
    preview_dominant_color?: string
    description: string
    developer?: string
    developer_id?: string
    how_to_play_text: string
    hasInAppPurchases: boolean
    seo_text: string
    is_hidden: boolean
    is_android: boolean
    is_ios: boolean
    is_desktop: boolean
    main_category: string
    tags: string[]
    categories: string[]
    meta: Array<'recommended' | 'ai_smart_ads' | 'ai_smart_ads-cloud-save' | 'seo_title_experiments'>
    series: string[]
    game_url: string
    engine: string[]
    screenshots: string[]
    links: UsefulLinkType[]
    video_metadata: APIGameVideoMetadataType[]
    videos: APIGameVideoType[]
    vertical: boolean
    config?: SmartAdsConfig
    horizontal: boolean
    source?: string
    rating: Rating
    createdAt: string
    updatedAt: string
    youtube_video: string
    gameUrlRedirectTarget?: GameRedirect
    badges: BadgeType[]
    meta_title?: string
    meta_description?: string
    is_in_export_catalog?: string
    supported_languages?: string[]
    age_rating?: AgeRating
    most_rated?: APIGameMostRatedType
    restricted_countries?: string[]
}

export type GameCategoriesInfoType = {
    categoriesInfo: CategoryShortInfo[]
    pathCategories: CategoryShortInfo[]
    collectionsInfo: CategoryShortInfo[]
}

type DebugInfoType = unknown & {
    DEBUG: { game_info: Record<string, unknown>; ranking_metrics: Record<string, unknown>[] }
}

export type ShortGameType = {
    hru: string
    preview: string
    icon?: string
    title: string
    videoId?: string
    videoUrl?: string
    hasInAppPurchases?: boolean
    debugInfo?: DebugInfoType
    badges?: BadgeType[]
    source: string
    platforms?: PlatformType[]
}

export type ShortGameTypePlaygamaTop = ShortGameType & {
    fod?: string
    color?: string
    likes?: string
}

export type ShortGameZipType = [
    hru: string,
    title: string,
    preview: string,
    icon: string | undefined,
    videoId: string | undefined,
    videoUrl: string | undefined,
    hasInAppPurchases: boolean,
    rankingDebug: DebugInfoType | undefined,
    badges: BadgeType[],
    source: string,
    platforms: PlatformType[] | undefined,
]

export type APIShortGameType = {
    hru: string
    icon: string
    id: string
    preview: string
    title: string
    videos: APIGameVideoType[]
    video_metadata: APIGameVideoMetadataType[]
    hasInAppPurchases: boolean
    rankingDebug?: { data: DebugInfoType }
    badges: BadgeType[]
    source: string
    platforms?: PlatformType[]
}

export type APICollectionGame = APIShortGameType &
    Pick<APIGameType, 'big_preview' | 'rating' | 'developer' | 'updatedAt' | 'main_category'> & {
        collection_description: string
        developer_id: number
        categoriesInfo: APICategoryShortInfo[]
    }

type APIGameVideoType = {
    player_url: string
    preview_url: string
    thumbnail_url: string
}

type APIGameVideoMetadataType = {
    type: 'gameplay'
    playgama_id: string
    external_url: string
}

export const MAIN_CATEGORIES = [
    'top_playgama',
    'io',
    'action',
    'puzzle',
    'arcade',
    'sports',
    'soccer',
    'strategy',
    'bubble-shooter',
    'girls',
    'boys',
    'car',
    'simulation',
    'shooting',
    'gun',
    'clicker',
    'funny',
    'snake',
    'skill',
    'drawing',
    'idle',
    'multiplayer',
    'solitaire',
    'horror',
    'two-player',
    'trending_now',
    'new',
    'random',
    'obby',
    'top_growth',
    'gta',
    'unblocked',
] as const

export type MainCategoriesType = (typeof MAIN_CATEGORIES)[number]

export type GameRecommendationType =
    | 'recommended'
    | 'top_playgama'
    | 'related'
    | 'random'
    | 'series'
    | 'developer'
    | 'linked_categories'

type APILinkedCategory = {
    category_type: CategoryType
    localizations: [
        {
            lang: AvailableLocale
            title: string
            hru: string
        },
    ]
}

export type LinkedCategory = { hru: string; categoryType: CategoryType; title: string }

export type APIGameRecommendation<T extends GameRecommendationType[]> = {
    [key in T[number]]: (key extends 'linked_categories' ? APILinkedCategory[] : APIShortGameType[]) | undefined
} & { slate_id?: string }

export type CategoryType = 'category' | 'tag' | 'series' | 'developer' | 'collection'

export type CategoryDataType = {
    categories: APICategoryData[]
    pagination: {
        page: 0
        pageSize: 0
    }
}

export type NoAdGamConfig = {
    basic: {
        1: number
        7: number
        30: number
    }
}

export type APIGeneralInfo = {
    categories: APICategoryShortInfo[]
    totalGamesCount: number
    noAdsConfigInGam: NoAdGamConfig
}

export type APICategoryDataType = {
    categories: APICategoryData[]
    pagination: {
        page: 0
        pageSize: 0
    }
}

export type APICategoryData = {
    id: string
    hru: string
    title: string
    categoryType: CategoryType
    games: APIShortGameType[]
    totalCount: number
}

export type APITrendingMainPage = {
    // slateId is present only when the user is assigned to a ranking experiment
    categories: (APICategoryData & { slateId?: string })[]
}

export type APIMobileFeed = {
    categories: (Omit<APICategoryData, 'categoryType' | 'games'> & {
        slateId: string
        games: (APIShortGameType & { position: number })[]
    })[]
    feedId: string
    pagination: { page: number; pageSize: number; hasMore: boolean }
}

type CategoryLocalization = {
    lang: AvailableLocale
    title: string
    id: string
    hru: string
}

export type APICategoryShortInfo = {
    id: string
    hru: string
    title: string
    categoryType: CategoryType
    totalCount?: number
    localizations?: CategoryLocalization[]
}

export type CategoryShortInfo = {
    id: string
    hru: string
    title: string
    href: RouteLocationRaw
    categoryType: CategoryType
    totalCount?: number
    localizations?: CategoryLocalization[]
}

export type APICategorySeoInfo =
    | {
          id: string
          hru: string
          title: string
          seoText: string
          seoPicture?: string
          description?: string // full name of categories
          subCategories?: { title: string; id: string; hru: string; categoryType: CategoryType }[]
          pathCategories?: { title: string; id: string; hru: string; categoryType: CategoryType }[]
          metaTitle?: string
          categoryType: CategoryType
          metaDescription?: string
          localizations: {
              lang: AvailableLocale
              hru: string
          }[]
      }
    | APIRedirect

export type CategoryInfo = {
    id: string
    hru: string
    title: string
    fullTitle: string
    seoText: string
    seoPicture?: string
    metaTitle?: string
    metaDescription?: string
    localizations?: {
        lang: AvailableLocale
        hru: string
    }[]
    subCategories: { title: string; id: string; href: RouteLocationRaw }[]
    pathCategories: { title: string; id: string; href: RouteLocationRaw }[]
}

export type FormSubmitData = {
    message: string
    email: string
    page?: string
    turnstileToken?: string
}

export type User = {
    _id: string
    firstName: string
    lastName: string
    avatar: string
    fennecs: number
    email?: string
    referralCode: string
    telegramAcc?: string
    tonWallet?: string
    gamesPlayed?: number
    createdAt?: number
    currentStreak?: number
    usersInvited?: number
    goldFenecBalance: number
    adsDisabledUntil?: string
}

export interface GameAttributes {
    clid?: string
    widgetId?: string
    userAcquisitionParams: Record<string, string>
}

export type AdditionalServerData = {
    locale: {
        detectedLocale?: AvailableLocale
    }
    config: VUE_APP_CONFIG
    renderTime: string
}

export type LinkTarget = '_blank' | '_self'

export type ImportWrapper = <T>(importCallback: () => T) => () => T

export const RedirectReason = {
    NO_LOCALE_IN_ROUTE: 'no_locale_in_route',
    PREFERRED_LOCALE: 'preferred_locale',
    REMOVE_EN_LOCALE: 'remove_en_locale',
    CATEGORY_TYPE_MISMATCH: 'category_type_mismatch',
    FIRST_CATEGORY_PAGE: 'first_category_page',
    PAGE_OUT_OF_BOUNDS: 'page_out_of_bounds',
    RANDOM_GAME_SET: 'random_game_set',
    SHOP_TO_PROFILE: 'shop_to_profile',
    MY_GAMES_TO_PROFILE: 'my_games_to_profile',
    COUNTRY_RESTRICTED: 'country_restricted',
} as const

export const RedirectMechanism = {
    FRONTEND_ROUTER: 'frontend_router',
    FRONTEND_REDIRECT: 'frontend_redirect',
    FRONTEND_CATEGORY_FALLBACK: 'frontend_category_fallback',
} as const

export type RankingParams = { clusterId?: string; audience?: string }

export type GoogleAdSettings = {
    type: AdProviderTypes.GAM_H5
    options: {
        gptSharedMode?: boolean
        headerBidding?: {
            bidders: HbBidderConfig[]
        }
        rewardedVideoAuction?: {
            bidders: HbBidderConfig[]
            timeoutMs?: number
        }
        apstag?: boolean
        name?: string
        forcedOutOfPageAdFormat?: OutOfPageAdType
        adUnitConfig: AdUnitConfig
        gamAccount: string
        gamSubAccount?: string
        timeout: number
        pageDomain: string
        platform: PlatformType
    }
}

export type GoogleInstreamSettings = {
    type: AdProviderTypes.GAM_INSTREAM
    options: {
        headerBidding?: {
            bidders: HbBidderConfig[]
        }
        adUnitConfig: AdUnitConfig
        gamAccount: string
        gamSubAccount?: string
        timeout: number
        pageURL: string
        platform: PlatformType
        useCurrentPageUrl?: boolean
    }
}

export type GameDistributionsSettings = {
    type: AdProviderTypes.GAME_DISTRIBUTION
    options: {
        timeout: number
        gameId?: string
    }
}

export type EmptyProviderSettings = {
    type: AdProviderTypes.EMPTY
    options: {
        timeout: number
    }
}

export type AppProviderSettings = {
    type: AdProviderTypes.APP
    options: {
        appNativeSDK?: AppNativeSDK
        platform: PlatformType
        timeout: number
        adUnitConfig: AdUnitConfig
    }
}

export type GoogleAdBannerSettings = {
    type: AdProviderTypes.GAM_BANNER_INTERSTITIAL
    options: {
        gptSharedMode?: boolean
        headerBidding?: {
            bidders: HbBidderConfig[]
        }
        apstag?: boolean
        timeout: number
        gamAccount: string
        gamSubAccount?: string
        pageDomain: string
        adUnitConfig: AdUnitConfig
        platform: PlatformType
    }
}

export type HeaderBiddingAdSettings = {
    type: AdProviderTypes.PREBID_DIRECT
    options: {
        timeout: number
        platform: PlatformType
        bidders: HbBidderConfig[]
    }
}

export type AdProvidersSettings =
    | GoogleAdSettings
    | GoogleInstreamSettings
    | GameDistributionsSettings
    | EmptyProviderSettings
    | AppProviderSettings
    | GoogleAdBannerSettings
    | HeaderBiddingAdSettings

export type EffectiveType = EffectiveConnectionType | 'not_applicable_ios' | 'not_applicable_other'
